import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { useCallback, useMemo, useState } from 'react';
import { dateFormatForAPI, formatDate } from 'utils/date';
import moment from 'utils/dayjs';

// The API returns startTime and endTime as timestamps when they
// should just be abstract times of day.  This causes problems with
// daylight savings.  So discard the date and time zone immediately.
//
const parseTime = (dateString: string) => {
  const [, hour, minutes] = dateString.match(/T(\d+):(\d+)/)!;
  return moment().hour(Number(hour)).minute(Number(minutes)).format('HH:mm');
};

interface ApiBookingSlot {
  name: string;
  startTime: string;
  endTime: string;
}

interface BookingSlot extends ApiBookingSlot {
  date: string;
}

const fixSlot = ({
  startTime,
  endTime,
  ...rest
}: ApiBookingSlot): BookingSlot => ({
  startTime: parseTime(startTime),
  endTime: parseTime(endTime),
  ...rest,
  date: formatDate(startTime, dateFormatForAPI),
});

const useBookingSlots = (
  dates: Date | string | Array<Date | string> = [],
  venueId: number | undefined = undefined
) => {
  const [bookingSlots, setBookingSlots] = useState<BookingSlot[]>([]);

  const apiRequest = useApiRequest();

  const requestValid =
    dates && (!Array.isArray(dates) || dates.length > 0) && venueId;

  const datesJson = useMemo(
    () =>
      JSON.stringify(
        Array.isArray(dates)
          ? dates.map(date => formatDate(date, dateFormatForAPI))
          : [formatDate(dates, dateFormatForAPI)]
      ),
    [dates]
  );

  const clearData = useCallback(async () => {
    setBookingSlots([]);
  }, []);

  const fetchData = useCallback(async () => {
    const {
      data: { bookingSlots: slots = [] },
    } = (await apiRequest.get(`/v1/venues/${venueId}/booking_slots`, {
      params: { dates: JSON.parse(datesJson) },
    })) as unknown as { data: { bookingSlots: ApiBookingSlot[] } };

    setBookingSlots(slots.map(fixSlot));
  }, [apiRequest, datesJson, venueId]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    !!requestValid
  );

  return { data: bookingSlots, loading, error, refresh };
};

export default useBookingSlots;
